import { InterpretationCardResponse } from 'types/api-responses';

export type Node = {
  key: string;
  name: string;
  transactionDate: string | null;
  url: string;
};

type Link = {
  key: string;
  from: string;
  to: string;
  documentTitle: string;
  transactionType: string;
  transactionDate: string | null;
  url?: string;
};

export type RunsheetGraphDataPoints = {
  nodes: Node[];
  links: Link[];
  // Set when the workspace exceeds the searchable window and the graph shows
  // only the pages the backend could serve without clamping.
  truncated?: boolean;
};

const extractRunsheetData = (
  runsheet: InterpretationCardResponse[]
): RunsheetGraphDataPoints => {
  const seenEntity = new Map<string, boolean>();
  const seenLink = new Set<string>();
  const nodes: Node[] = [];
  const links: Link[] = [];

  runsheet.forEach((interpretation) => {
    const transactions = interpretation.transactions;

    if (!transactions.length) return;

    const transactionDate = interpretation.interpretationDate;

    transactions.forEach((transaction) => {
      // Scope grantors to their own transaction: an interpretation with two
      // transactions (G1->C1, G2->C2) must not emit G1->C2, an edge that never
      // existed. seenLink keys include transaction.id, so this stays deduped.
      const grantorIds: string[] = [];

      transaction.grantors.forEach((grantor) => {
        // Only the { id: null, deleted: true } sentinel (a merged party with no
        // surviving canonical) has no node to draw; a soft-deleted entity with a
        // real id keeps its name and href. Mirrors the card list's filter.
        if (grantor.entity.id === null) return;
        // An unaliased entity is its own canonical, so fall back to its own id;
        // String(null/undefined) would otherwise collapse every such party onto
        // one bogus node.
        const canonicalId = String(
          grantor.entity.canonicalId ?? grantor.entity.id
        );
        const entity = {
          key: canonicalId,
          id: canonicalId,
          name: grantor.entity.name,
          url: grantor.entity.href,
          transactionDate,
        };

        if (!seenEntity.get(canonicalId)) {
          nodes.push(entity);
          seenEntity.set(canonicalId, true);
        }
        grantorIds.push(canonicalId);
      });

      transaction.conveyances.forEach((conveyance) => {
        if (conveyance.entity.id === null) return;
        const canonicalId = String(
          conveyance.entity.canonicalId ?? conveyance.entity.id
        );
        const entity = {
          key: canonicalId,
          id: canonicalId,
          name: conveyance.entity.name,
          transactionDate,
          url: conveyance.entity.href,
        };

        if (!seenEntity.get(canonicalId)) {
          nodes.push(entity);
          seenEntity.set(canonicalId, true);
        }

        grantorIds.forEach((grantorId) => {
          // Key each edge by its identity so a re-served page (or repeated
          // grantor) can't spawn a parallel edge that double-counts.
          const key = `${grantorId}-${canonicalId}-${transaction.id}`;
          if (seenLink.has(key)) return;
          seenLink.add(key);
          links.push({
            key,
            from: grantorId,
            to: canonicalId,
            documentTitle: interpretation.document.documentTitle ?? '',
            transactionType: transaction.type,
            transactionDate,
            url: interpretation.href,
          });
        });
      });
    });
  });

  return { nodes, links };
};

export { extractRunsheetData };
